// Password Service with Secure Hashing
import CryptoJS from 'crypto-js';
import { supabase } from '../supabase-client';
import securitySyncService from './SecuritySyncService';
import SecurePasswordHash from './SecurePasswordHash';

class PasswordService {
  constructor() {
    this.minLength = 8;
    this.maxLength = 128;
    this.hashAlgorithm = 'SHA256';
  }

  // Hash password securely
  static async hashPassword(plainPassword) {
    try {
      // Use SHA-256 for backward compatibility with existing system
      const hash = CryptoJS.SHA256(plainPassword).toString();
      return hash;
    } catch (error) {
      throw new Error('Password hashing failed');
    }
  }

  // Verify password
  static async verifyPassword(plainPassword, hashedPassword) {
    try {
      const computedHash = await this.hashPassword(plainPassword);
      return computedHash === hashedPassword;
    } catch (error) {
      return false;
    }
  }

  // Validate password strength
  static validatePasswordStrength(password) {
    if (!password || typeof password !== 'string') {
      return {
        isValid: false,
        strength: 0,
        checks: {
          minLength: false,
          hasUpper: false,
          hasLower: false,
          hasNumber: false,
          hasSpecial: false
        },
        suggestions: ['Password is required']
      };
    }

    const checks = {
      minLength: password.length >= 8,
      maxLength: password.length <= 128,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };

    const passedChecks = Object.values(checks).filter(Boolean).length;
    const strength = Math.min(passedChecks, 5);

    const suggestions = [];
    if (!checks.minLength) suggestions.push('Use at least 8 characters');
    if (!checks.maxLength) suggestions.push('Use no more than 128 characters');
    if (!checks.hasUpper) suggestions.push('Include uppercase letters');
    if (!checks.hasLower) suggestions.push('Include lowercase letters');
    if (!checks.hasNumber) suggestions.push('Include numbers');
    if (!checks.hasSpecial) suggestions.push('Include special characters');

    return {
      isValid: passedChecks >= 4, // Require at least 4 checks
      strength,
      checks,
      suggestions: suggestions.length > 0 ? suggestions : ['Password strength is good']
    };
  }

  // Generate secure random password
  static generateSecurePassword(length = 16) {
    try {
      const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
      let password = '';

      for (let i = 0; i < length; i++) {
        const randomIndex = CryptoJS.lib.WordArray.random(1).words[0] % charset.length;
        password += charset[Math.abs(randomIndex) % charset.length];
      }

      return password;
    } catch (error) {
      throw new Error('Password generation failed');
    }
  }

  // Check if password needs to be changed
  static async checkPasswordExpiry(userId) {
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('password_changed_at, created_at')
        .eq('id', userId)
        .single();

      if (!userData) {
        return { mustChange: false, daysRemaining: 90 };
      }

      const lastChange = new Date(userData.password_changed_at || userData.created_at);
      const daysSinceChange = Math.floor((Date.now() - lastChange.getTime()) / (1000 * 60 * 60 * 24));
      const passwordExpiryDays = 90; // 90 days policy

      if (daysSinceChange >= passwordExpiryDays) {
        return { mustChange: true, daysRemaining: 0 };
      }

      return {
        mustChange: false,
        daysRemaining: passwordExpiryDays - daysSinceChange
      };
    } catch (error) {
      return { mustChange: false, daysRemaining: 90 };
    }
  }

  // Change password - SECURE VERSION with sync
  static async changePassword(userId, currentPassword, newPassword, username = null) {
    try {
      // Get current user data (tentar Supabase primeiro)
      let userData = null;
      let userFromSupabase = false;

      try {
        const { data: supabaseData, error: supabaseError } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();

        if (!supabaseError && supabaseData) {
          userData = supabaseData;
          userFromSupabase = true;
        }
      } catch (supabaseErr) {
        console.log('Supabase não disponível, usando sistema local');
      }

      // Se não encontrou no Supabase, buscar no sistema local
      if (!userData) {
        const legacyUsers = JSON.parse(localStorage.getItem('INIT_USERS') || '[]');
        userData = legacyUsers.find(u => u.id === userId);
      }

      if (!userData) {
        throw new Error('User not found');
      }

      // Determinar username se não fornecido
      const effectiveUsername = username || userData.username;

      // Verify current password (para usuários com hash)
      const currentPasswordHash = userData.passwordHash || userData.password_hash;
      if (currentPasswordHash) {
        const isValid = SecurePasswordHash.verifyPassword(
          currentPassword,
          currentPasswordHash,
          effectiveUsername
        );

        if (!isValid) {
          throw new Error('Current password is incorrect');
        }
      } else {
        // Se ainda em texto plano (compatibilidade antiga)
        if (userData.password && userData.password !== currentPassword) {
          throw new Error('Current password is incorrect');
        }
      }

      // Validate new password
      const passwordValidation = this.validatePasswordStrength(newPassword);
      if (!passwordValidation.isValid) {
        throw new Error(`Weak password: ${passwordValidation.suggestions.join(', ')}`);
      }

      // Hash new password usando sistema seguro
      const hashData = SecurePasswordHash.hashForStorage(newPassword, effectiveUsername);
      const hashedNewPassword = hashData.hash;

      // Update no Supabase se disponível
      if (userFromSupabase) {
        const { error: updateError } = await supabase
          .from('users')
          .update({
            password_hash: hashedNewPassword,
            password_salt: hashData.salt,
            password_changed_at: new Date().toISOString(),
            password_version: '2.0'
          })
          .eq('id', userId);

        if (updateError) {
          console.error('Erro ao atualizar no Supabase:', updateError);
          // Continuar com atualização local
        } else {
          // Atualizar Supabase auth password
          try {
            const { error: authError } = await supabase.auth.updateUser({
              password: newPassword
            });

            if (authError && authError.message !== 'Auth session missing!') {
              console.error('Erro ao atualizar auth Supabase:', authError);
            }
          } catch (authErr) {
            console.error('Erro ao atualizar auth Supabase:', authErr);
          }
        }
      }

      // Update no sistema local (INIT_USERS)
      const legacyUsers = JSON.parse(localStorage.getItem('INIT_USERS') || '[]');
      const userIndex = legacyUsers.findIndex(u => u.id === userId);

      if (userIndex !== -1) {
        // Atualizar com novo hash seguro
        legacyUsers[userIndex].passwordHash = hashData.hash;
        legacyUsers[userIndex].passwordSalt = hashData.salt;
        legacyUsers[userIndex].passwordVersion = '2.0';
        legacyUsers[userIndex].passwordUpdatedAt = new Date().toISOString();
        legacyUsers[userIndex].passwordUpdatedBy = 'self';

        // Remover senha em texto plano se existir
        if (legacyUsers[userIndex].password) {
          delete legacyUsers[userIndex].password;
        }

        // Salvar dados atualizados
        localStorage.setItem('INIT_USERS', JSON.stringify(legacyUsers));

        console.log('✅ Senha atualizada no sistema local com hash seguro');
      }

      // Sincronizar com servidor (funciona mesmo sem Supabase)
      await securitySyncService.updatePassword(userId, newPassword, effectiveUsername);

      // Log password change
      await this.logPasswordChange(userId, 'PASSWORD_CHANGED');

      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao mudar senha:', error);
      return { success: false, error: error.message };
    }
  }

  // Admin reset password
  static async adminResetPassword(adminId, userId, newPassword) {
    try {
      // Verify admin permissions
      const { data: adminData } = await supabase
        .from('users')
        .select('role')
        .eq('id', adminId)
        .single();

      if (!adminData || adminData.role !== 'admin') {
        throw new Error('Only administrators can reset passwords');
      }

      // Validate new password
      const passwordValidation = this.validatePasswordStrength(newPassword);
      if (!passwordValidation.isValid) {
        throw new Error(`Weak password: ${passwordValidation.suggestions.join(', ')}`);
      }

      // Hash new password
      const hashedNewPassword = await this.hashPassword(newPassword);

      // Update user password
      const { error: updateError } = await supabase
        .from('users')
        .update({
          password_hash: hashedNewPassword,
          password_changed_at: new Date().toISOString(),
          must_change_password: true,
          password_reset_by: adminId
        })
        .eq('id', userId);

      if (updateError) {
        throw new Error(`Password reset failed: ${updateError.message}`);
      }

      // Log password reset
      await this.logPasswordChange(userId, 'PASSWORD_RESET_BY_ADMIN', adminId);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Log password change
  static async logPasswordChange(userId, eventType, adminId = null) {
    try {
      await supabase.from('audit_logs').insert({
        id: generateUUID(),
        event_type: eventType,
        user_id: adminId || userId,
        target_user_id: userId,
        timestamp: new Date().toISOString(),
        ip_address: 'localhost' // Will be updated by server
      });
    } catch (error) {
      // Silent fail - don't block operation
    }
  }
}

// Helper function to generate UUID
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

export default PasswordService;
